import React from 'react';
import StatCard from '../StatCard';
import RmaStagePill from './RmaStagePill';
import { DeviceTypeChip, Mono, Th, EmptyState } from './RmaBits';
import {
  stageOf, isOpen, isOverdue,
  stageAgeDays, ageTone, TONE, medianTurnaroundDays, unitsAwayFromStock,
} from './rmaHelpers';

// Screen 1 — the RMA queue. Reads only; every action calls up into RmaModule, so a mis-click
// here can never trigger a whole-state write to Azure.

const COLS = '132px minmax(180px,1fr) minmax(168px,1.1fr) 150px 68px 140px 32px';

// The Open tab is the union of the three open stages. Overdue is an orthogonal predicate
// over that union, not a stage of its own — so an overdue RMA appears under both
// "With manufacturer" and "Overdue", and the tab counts deliberately do not sum to Open.
const TABS = [
  { key: 'open', label: 'Open' },
  { key: 'awaiting_dispatch', label: 'Awaiting dispatch' },
  { key: 'with_manufacturer', label: 'With manufacturer' },
  { key: 'returned_to_bench', label: 'Returned to bench' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'closed', label: 'Closed' },
];

const SORTS = [
  { value: 'stage_age_desc', label: 'Oldest in stage' },
  { value: 'stage_age_asc', label: 'Newest in stage' },
  { value: 'raised_desc', label: 'Recently raised' },
  { value: 'units_desc', label: 'Most units' },
];

const matchesTab = (sh, tab, now, settings) => {
  if (tab === 'open') return isOpen(sh);
  if (tab === 'overdue') return isOverdue(sh, now, settings);
  return stageOf(sh) === tab;
};

function RmaQueue({
  st, shipments, devicesById, isReadOnly,
  onOpen, onNew, onReceive, onExport,
  FilterSelect, SegmentedControl, darkMode, OW, DM,
  now = Date.now(),
}) {
  const [tab, setTab] = React.useState('open');
  const [sort, setSort] = React.useState('stage_age_desc');
  const [page, setPage] = React.useState(1);
  const [rows, setRows] = React.useState(8);
  const [search, setSearch] = React.useState('');

  const settings = st.rmaSettings;

  const counts = React.useMemo(() => Object.fromEntries(
    TABS.map(t => [t.key, shipments.filter(sh => matchesTab(sh, t.key, now, settings)).length])
  ), [shipments, now, settings]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = shipments.filter(sh => matchesTab(sh, tab, now, settings));
    if (q) {
      out = out.filter(sh => {
        if ((sh.ref || '').toLowerCase().includes(q)) return true;
        if ((sh.faultCategory || '').toLowerCase().includes(q)) return true;
        if ((sh.trackingNumber || '').toLowerCase().includes(q)) return true;
        return (sh.deviceIds || []).some(id => (devicesById.get(id)?.serial || '').toLowerCase().includes(q));
      });
    }
    const by = {
      stage_age_desc: (a, b) => stageAgeDays(b, now) - stageAgeDays(a, now),
      stage_age_asc: (a, b) => stageAgeDays(a, now) - stageAgeDays(b, now),
      raised_desc: (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
      units_desc: (a, b) => (b.deviceIds || []).length - (a.deviceIds || []).length,
    }[sort];
    return [...out].sort(by);
  }, [shipments, tab, search, sort, now, settings, devicesById]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / rows));
  const curPage = Math.min(page, pageCount);
  const from = (curPage - 1) * rows;
  const pageRows = filtered.slice(from, from + rows);

  // Tiles
  const tiles = React.useMemo(() => {
    const open = shipments.filter(isOpen);
    const awaiting = shipments.filter(sh => stageOf(sh) === 'awaiting_dispatch');
    const over = shipments.filter(sh => isOverdue(sh, now, settings));
    const awayUnits = unitsAwayFromStock(shipments);
    const awaitingUnits = awaiting.reduce((n, sh) => n + (sh.deviceIds || []).length, 0);
    const longest = over.reduce((m, sh) => Math.max(m, stageAgeDays(sh, now)), 0);
    const median = medianTurnaroundDays(shipments, 90, now);
    const mfr = settings?.manufacturer || 'the manufacturer';
    return [
      { label: 'Open RMAs', value: String(open.length), sub: awayUnits + (awayUnits === 1 ? ' unit' : ' units') + ' away from stock', tab: 'open' },
      { label: 'Awaiting dispatch', value: String(awaiting.length), sub: awaitingUnits ? awaitingUnits + ' units boxed, no label yet' : 'nothing waiting on a label', tab: 'awaiting_dispatch' },
      { label: 'Over SLA', value: String(over.length), sub: over.length ? 'longest ' + longest + ' days with ' + mfr : 'all within agreed turnaround', tab: 'overdue', danger: over.length > 0 },
      // Null, not 0 — with no closed history yet, "0 days" would be a lie.
      { label: 'Median turnaround', value: median === null ? '—' : String(median), sub: median === null ? 'no returns in the last 90 days' : 'days, last 90 days' },
    ];
  }, [shipments, now, settings]);

  const goTab = (k) => { setTab(k); setPage(1); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.3px', color: 'var(--text-primary)' }}>
            Returns to manufacturer
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-subtle)', marginTop: 4 }}>
            Faulty units raised, shipped, and reconciled against warranty.
            {settings?.manufacturer ? ' Manufacturer: ' + settings.manufacturer + '.' : ''}
          </div>
        </div>
        {!isReadOnly && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <button onClick={onReceive} className="btn btn-ghost">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" /><path d="M3 8l9 5 9-5" />
              </svg>
              Receive returns
            </button>
            <button onClick={onNew} className="btn btn-primary">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              New RMA
            </button>
          </div>
        )}
      </div>

      {/* Tiles */}
      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
        {tiles.map(t => (
          <StatCard
            key={t.label}
            label={t.label}
            value={t.value}
            valueColor={t.danger ? 'var(--danger-text)' : undefined}
            sub={t.sub}
            onClick={t.tab ? () => goTab(t.tab) : undefined}
          />
        ))}
      </div>

      {/* Filter bar — the stock page's toolbar, matched control for control: 32px scan
          field, one segmented run of states, inline dropdowns, then icon buttons pushed
          right. Stage counts ride inside the segments rather than on separate tabs. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        background: 'var(--surface-default)', border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)', padding: '12px 16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', background: 'var(--surface-default)', width: 280, maxWidth: '100%' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--brand-light)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><line x1="7" y1="12" x2="17" y2="12" />
          </svg>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Scan or type a serial, ref or tracking"
            aria-label="Scan or search RMAs"
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, color: 'var(--text-primary)' }}
          />
          {search && (
            <button onClick={() => { setSearch(''); setPage(1); }} aria-label="Clear search"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', padding: 0, display: 'flex', alignItems: 'center' }}>&#x2715;</button>
          )}
        </div>

        <div style={{ display: 'inline-flex', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          {TABS.map((t, i) => {
            const on = tab === t.key;
            return (
              <button
                key={t.key} onClick={() => goTab(t.key)} aria-pressed={on}
                style={{
                  height: 32, padding: '0 12px', border: 'none',
                  borderLeft: i ? '1px solid var(--border-default)' : 'none',
                  background: on ? 'var(--text-primary)' : 'var(--surface-default)',
                  color: on ? '#fff' : 'var(--text-muted)',
                  fontWeight: on ? 600 : 400, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer',
                  whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {t.label}
                <span style={{ fontSize: 11, fontWeight: 600, opacity: on ? 0.85 : 0.6 }}>{counts[t.key]}</span>
              </button>
            );
          })}
        </div>

        <FilterSelect value={sort} onChange={setSort} options={SORTS} darkMode={darkMode} OW={OW} DM={DM} />

        <div style={{ flex: 1 }} />

        <button
          title="Export" aria-label="Export" onClick={() => onExport(filtered)}
          style={{ width: 32, height: 32, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
                   background: 'var(--surface-default)', color: 'var(--text-muted)',
                   display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
        </button>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, padding: '11px 18px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-inset)' }}>
          <Th>RMA</Th><Th>Devices</Th><Th>Fault</Th><Th>Stage</Th><Th align="right">In stage</Th><Th>Tracking</Th><div />
        </div>

        {pageRows.length === 0 && (
          <EmptyState title={search ? 'Nothing matches that search' : 'No RMAs in this stage'}>
            {search
              ? 'Try a different reference, serial or tracking number.'
              : 'Units raised for return will appear here as they move through the process.'}
          </EmptyState>
        )}

        {pageRows.map(sh => {
          const devs = (sh.deviceIds || []).map(id => devicesById.get(id)).filter(Boolean);
          const age = stageAgeDays(sh, now);
          const tone = TONE[ageTone(age)];
          const types = [...new Set(devs.map(d => d.type).filter(Boolean))];
          const shown = devs.slice(0, 2);
          const extra = (sh.deviceIds || []).length - shown.length;
          const fault = sh.faultMode === 'per_device'
            ? (types.length ? 'Per device' : 'Per device')
            : (sh.faultCategory || '—');
          const note = sh.faultMode === 'per_device'
            ? Object.values(sh.faultCategories || {}).filter(Boolean).join(', ') || 'Mixed faults'
            : (Object.values(sh.faultDescriptions || {})[0] || '');
          return (
            <div
              key={sh.id}
              onClick={() => onOpen(sh.id)}
              style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-raised)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div>
                <Mono>{sh.ref || sh.id}</Mono>
                <div style={{ fontSize: 11, color: 'var(--text-ghost)', marginTop: 3 }}>
                  {sh.createdAt ? 'raised ' + new Date(sh.createdAt).toLocaleDateString('en-GB') : '—'}
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5 }}>
                <span className="chip" style={{ fontWeight: 600 }}>{(sh.deviceIds || []).length} units</span>
                {types.length === 1
                  ? <DeviceTypeChip type={types[0]} />
                  : types.length > 1 && <span className="chip">{types.length} types</span>}
                {shown.map(d => <Mono key={d.id} size={11} weight={400} color="var(--text-subtle)">{d.serial}</Mono>)}
                {extra > 0 && <span style={{ fontSize: 11, color: 'var(--text-ghost)' }}>+{extra} more</span>}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-default)' }}>{fault}</div>
                <div style={{ fontSize: 11, color: 'var(--text-ghost)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note}</div>
              </div>
              <div>
                <RmaStagePill shipment={sh} settings={settings} now={now} />
                {/* Says out loud that an undispatched RMA is still a work in progress —
                    otherwise "Awaiting dispatch" reads as finished-and-waiting rather than
                    as something you can still open and change. */}
                {stageOf(sh) === 'awaiting_dispatch' && (
                  <div style={{ fontSize: 10.5, color: 'var(--text-ghost)', marginTop: 4 }}>
                    open to add or remove units
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <Mono size={13} color={tone.text === TONE.neutral.text ? 'var(--text-default)' : tone.text}>{age}</Mono>
                <div style={{ fontSize: 10.5, color: 'var(--text-ghost)', marginTop: 2 }}>days</div>
              </div>
              <div style={{ minWidth: 0 }}>
                {sh.trackingNumber
                  ? <Mono size={11.5} weight={400} color="var(--info-text)">{sh.trackingNumber}</Mono>
                  : <span style={{ fontSize: 11.5, color: 'var(--text-ghost)' }}>—</span>}
                <div style={{ fontSize: 11, color: 'var(--text-ghost)', marginTop: 3 }}>{sh.carrier || 'no carrier set'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: 'var(--border-hover)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 6l6 6-6 6" /></svg>
              </div>
            </div>
          );
        })}

        {/* Footer: range, rows-per-page, pagination */}
        {filtered.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '11px 18px', background: 'var(--surface-inset)', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
              {from + 1}–{Math.min(from + rows, filtered.length)} of {filtered.length} RMAs
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-subtle)' }}>Rows</span>
                <SegmentedControl
                  value={rows}
                  onChange={n => { setRows(n); setPage(1); }}
                  options={[8, 25, 50].map(n => ({ value: n, label: String(n) }))}
                />
              </div>
              {pageCount > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button onClick={() => setPage(Math.max(1, curPage - 1))} disabled={curPage === 1} className="btn btn-sm btn-ghost" aria-label="Previous page">‹</button>
                  <span style={{ fontSize: 12, color: 'var(--text-subtle)', padding: '0 4px' }}>{curPage} of {pageCount}</span>
                  <button onClick={() => setPage(Math.min(pageCount, curPage + 1))} disabled={curPage === pageCount} className="btn btn-sm btn-ghost" aria-label="Next page">›</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RmaQueue;
export { TABS as RMA_QUEUE_TABS, matchesTab };
